import React, { useState, useEffect, useRef } from "react";
import HeroHeader from "../components/HeroHeader";
import DiscordIcon from "../images/socials/icon-discord.png";
import InstagramIcon from "../images/socials/icon-instagram.png";
import LinkedinIcon from "../images/socials/icon-linkedIn.png";
import TiktokIcon from "../images/socials/icon-tiktok.png";
import XIcon from "../images/socials/icon-x.png";

const C = {
  bg: "#0A0E17",
  surface: "#111827",
  surfaceHover: "#1A2235",
  card: "#151D2E",
  cardBorder: "#1E2A3F",
  primary: "#19E094",
  primaryMuted: "rgba(25, 224, 148, 0.12)",
  primaryGlow: "rgba(25, 224, 148, 0.25)",
  under: "#FF4D6A",
  underMuted: "rgba(255, 77, 106, 0.12)",
  textPrimary: "#F1F5F9",
  textSecondary: "#8B95A8",
  textMuted: "#4A5568",
  border: "#1C2536",
};

const sectionInner = { maxWidth: 1200, margin: "0 auto", padding: "100px 24px" };

/* ── Scroll-triggered fade-in ── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, visible] = useInView(0.1);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>{children}</div>
  );
}

/* ── Step card ── */
function StepCard({ number, title, description, delay }) {
  return (
    <FadeIn delay={delay} style={{ flex: "1 1 260px" }}>
      <div style={{
        background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 20,
        padding: "36px 28px", height: "100%", display: "flex", flexDirection: "column",
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14, background: C.primaryMuted,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, fontWeight: 800, color: C.primary, fontFamily: "'Space Mono', monospace",
          marginBottom: 20, flexShrink: 0,
        }}>{number}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, fontWeight: 400 }}>{description}</div>
      </div>
    </FadeIn>
  );
}

/* ── Feature card ── */
function FeatureCard({ icon, title, description, delay }) {
  return (
    <FadeIn delay={delay} style={{ flex: "1 1 280px" }}>
      <div style={{
        background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 20,
        padding: "36px 28px", height: "100%",
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, background: C.primaryMuted,
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 20, fontSize: 24,
        }}>{icon}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6 }}>{description}</div>
      </div>
    </FadeIn>
  );
}

/* ── FAQ item ── */
function FAQItem({ question, answer, delay }) {
  const [open, setOpen] = useState(false);
  return (
    <FadeIn delay={delay}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          background: C.card, borderRadius: 16, padding: "22px 28px",
          cursor: "pointer", transition: "border-color 0.2s ease",
          border: `1.5px solid ${open ? "rgba(25, 224, 148, 0.35)" : C.cardBorder}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 17, fontWeight: 600, color: C.textPrimary }}>{question}</span>
          <span style={{
            color: C.primary, fontSize: 22, fontWeight: 300,
            transition: "transform 0.3s ease",
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
            flexShrink: 0, marginLeft: 16,
          }}>+</span>
        </div>
        <div style={{
          maxHeight: open ? 400 : 0, overflow: "hidden",
          transition: "max-height 0.4s cubic-bezier(0.16,1,0.3,1)",
        }}>
          <div style={{ fontSize: 15, color: C.textSecondary, lineHeight: 1.7, paddingTop: 14 }}>{answer}</div>
        </div>
      </div>
    </FadeIn>
  );
}

/* ── Stat block ── */
function StatBlock({ value, label, delay }) {
  return (
    <FadeIn delay={delay} style={{ textAlign: "center", flex: "1 1 140px" }}>
      <div style={{
        fontSize: 36, fontWeight: 800, color: C.primary,
        fontFamily: "'Space Mono', monospace", marginBottom: 4, whiteSpace: "nowrap",
      }}>{value}</div>
      <div style={{ fontSize: 14, color: C.textSecondary, fontWeight: 500 }}>{label}</div>
    </FadeIn>
  );
}

/* ── Contest slip (shared by hero + points sections) ── */
function ContestSlip({ avatar, avatarBg, avatarColor, name, total, totalColor, picks, footer }) {
  return (
    <div style={{
      flex: 1, background: C.card, borderRadius: 20, padding: "22px 20px",
      border: footer.winner ? `1.5px solid rgba(25, 224, 148, 0.25)` : `1px solid ${C.cardBorder}`,
      boxShadow: footer.winner ? `0 0 40px ${C.primaryGlow}` : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, background: avatarBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 800, color: avatarColor, fontFamily: "'Space Mono', monospace",
        }}>{avatar}</div>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{name}</span>
        <span style={{ marginLeft: "auto", fontSize: 20, fontWeight: 800, color: totalColor, fontFamily: "'Space Mono', monospace" }}>{total}</span>
        <span style={{ fontSize: 10, color: C.textMuted }}>pts</span>
      </div>
      {picks.map((p, i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "9px 12px", borderRadius: 10,
          background: i % 2 === 0 ? C.surfaceHover : "transparent", marginBottom: 3,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {p.jersey && (
              <div style={{
                width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                background: p.teamColor || C.surfaceHover,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 800, color: "#ffffff",
                fontFamily: "'Space Mono', monospace",
              }}>{p.jersey}</div>
            )}
            <span style={{ fontSize: 13, color: p.correct ? C.primary : C.under }}>{p.correct ? "✓" : "✕"}</span>
            <span style={{ fontSize: 13, color: C.textSecondary }}>{p.pick}</span>
          </div>
          <span style={{
            fontSize: 13, fontWeight: 700, fontFamily: "'Space Mono', monospace",
            color: p.correct ? C.primary : C.textMuted,
            textDecoration: p.correct ? "none" : "line-through",
          }}>{p.pts}</span>
        </div>
      ))}
      <div style={{
        marginTop: 14, padding: "10px 14px", borderRadius: 10, textAlign: "center",
        background: footer.winner ? C.primaryMuted : C.underMuted,
        border: footer.winner ? `1px solid rgba(25, 224, 148, 0.2)` : "none",
      }}>
        <span style={{ fontSize: 13, fontWeight: footer.winner ? 700 : 600, color: footer.winner ? C.primary : C.under }}>
          {footer.label}
        </span>
      </div>
    </div>
  );
}

const YOU_PICKS = [
  { jersey: "0", teamColor: "#007A33", pick: "Tatum Over 28.5 pts", pts: 215, correct: true },
  { jersey: "97", teamColor: "#FF4C00", pick: "McDavid Over 1.5 ast", pts: 240, correct: false },
  { jersey: "77", teamColor: "#00538C", pick: "Dončić Under 8.5 ast", pts: 215, correct: true },
];
const OPP_PICKS = [
  { jersey: "15", teamColor: "#C8102E", pick: "Capela Over 9.5 reb", pts: 195, correct: true },
  { jersey: "11", teamColor: "#FCB514", pick: "Heinen Over 2.5 sog", pts: 205, correct: false },
  { jersey: "38", teamColor: "#002654", pick: "Jenner Under 0.5 pts", pts: 215, correct: false },
];

/* ════════════════════════════════════════
   HOME PAGE
   ════════════════════════════════════════ */
export default function Home() {
  return (
    <div id="home" style={{ overflowX: "hidden" }}>

      {/* ── SECTION 1: HERO ── */}
      <section style={{ position: "relative", paddingTop: 160, paddingBottom: 100, background: C.bg, overflow: "hidden" }}>
        {/* Glow */}
        <div style={{
          position: "absolute", top: -200, left: "50%", transform: "translateX(-50%)",
          width: 900, height: 900, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(25, 224, 148, 0.07) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 60, flexWrap: "wrap" }}>

            {/* Left: copy */}
            <div style={{ flex: "1 1 400px", minWidth: 0 }}>
              <FadeIn>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: C.primaryMuted, padding: "8px 16px", borderRadius: 9999,
                  marginBottom: 24,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.primary, display: "block", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>Peer-to-Peer Fantasy Sports</span>
                </div>
              </FadeIn>
              <FadeIn delay={0.1}>
                <h1 style={{
                  fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 800, lineHeight: 1.08,
                  letterSpacing: "-0.03em", marginBottom: 24, color: C.textPrimary,
                }}>
                  Pick players.<br />
                  Beat people.<br />
                  <span style={{ color: C.primary }}>Not the house.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.2}>
                <p style={{
                  fontSize: 18, lineHeight: 1.65, color: C.textSecondary,
                  maxWidth: 480, marginBottom: 36, fontWeight: 400,
                }}>
                  Parlaye matches you against other players, not algorithms. Pick 3 player props, earn points for correct calls, and whoever scores higher wins. No perfect parlays needed.
                </p>
              </FadeIn>
              <FadeIn delay={0.3}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <a
                    href="#download"
                    style={{
                      background: `linear-gradient(135deg, ${C.primary}, #0fa968)`,
                      color: C.bg, padding: "16px 36px", borderRadius: 14,
                      fontSize: 16, fontWeight: 700, textDecoration: "none",
                      boxShadow: `0 4px 16px ${C.primaryGlow}`, display: "inline-block",
                    }}
                  >
                    Download
                  </a>
                  <a
                    href="#how-it-works"
                    style={{
                      color: C.primary, padding: "16px 28px", borderRadius: 14,
                      fontSize: 16, fontWeight: 600, textDecoration: "none",
                      border: `1.5px solid rgba(25, 224, 148, 0.3)`, display: "inline-block",
                    }}
                  >
                    See How It Works
                  </a>
                </div>
              </FadeIn>
            </div>

            {/* Right: contest slips */}
            <div style={{ flex: "1 1 340px", minWidth: 0 }}>
              <FadeIn delay={0.25}>
                <div className="hero-contest-slips" style={{ display: "flex", gap: 14, alignItems: "stretch" }}>
                  <ContestSlip
                    avatar="Y" avatarBg={C.primaryMuted} avatarColor={C.primary}
                    name="You" total="430" totalColor={C.primary}
                    picks={YOU_PICKS}
                    footer={{ winner: true, label: "Winner 🎉" }}
                  />
                  <div className="hero-vs-divider" style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, alignSelf: "center",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", background: C.surfaceHover,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 800, color: C.textMuted, fontFamily: "'Space Mono', monospace",
                    }}>VS</div>
                  </div>
                  <ContestSlip
                    avatar="O" avatarBg={C.underMuted} avatarColor={C.under}
                    name="Opponent" total="195" totalColor={C.textMuted}
                    picks={OPP_PICKS}
                    footer={{ winner: false, label: "Loser" }}
                  />
                </div>
              </FadeIn>
            </div>

          </div>
        </div>
      </section>

      {/* ── SECTION 2: STATS BAR ── */}
      <section style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{
          maxWidth: 1200, margin: "0 auto", padding: "48px 24px",
          display: "flex", justifyContent: "space-around", alignItems: "center",
          gap: 24, flexWrap: "wrap",
        }}>
          <StatBlock value="$200K+" label="Saved for our players" delay={0} />
          <StatBlock value="180K" label="Picks made" delay={0.1} />
          <StatBlock value="60,000" label="Entries submitted" delay={0.2} />
          <StatBlock value="3,500+" label="Players and counting" delay={0.3} />
        </div>
      </section>

      {/* ── SECTION 3: HOW IT WORKS ── */}
      <section id="how-it-works" style={{ background: C.bg }}>
        <div style={sectionInner}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h2 style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800,
                letterSpacing: "-0.02em", marginBottom: 14, color: C.textPrimary,
              }}>
                Three picks. One opponent.{" "}
                <span style={{ color: C.primary }}>That's it.</span>
              </h2>
              <p style={{ fontSize: 18, color: C.textSecondary, maxWidth: 560, margin: "0 auto", lineHeight: 1.6 }}>
                No salary caps. No 12-player lineups. Just pick 3 player props and see if you outscore your opponent.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <StepCard number="1" title="Browse Player Props" description="Scroll through tonight's players across NBA, NHL, soccer, and more. Every prop shows a stat line and points for over and under." delay={0.1} />
            <StepCard number="2" title="Pick 3 Over/Unders" description="Tap over or under on 3 different player props. Higher-risk picks are worth more points, so you control your strategy." delay={0.2} />
            <StepCard number="3" title="Get Matched and Compete" description="We match you against another player with their own 3 picks. Whoever earns more points from correct calls wins the contest." delay={0.3} />
          </div>
        </div>
      </section>

      {/* ── SECTION 4: COMPARISON ── */}
      <section style={{ background: C.surface }}>
        <div style={sectionInner}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h2 style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800,
                letterSpacing: "-0.02em", marginBottom: 14, color: C.textPrimary,
              }}>
                Why this is <span style={{ color: C.primary }}>better</span>
              </h2>
              <p style={{ fontSize: 18, color: C.textSecondary, maxWidth: 580, margin: "0 auto", lineHeight: 1.6 }}>
                Traditional DFS apps profit when you lose. On Parlaye, we only earn a small fee from contests. Our incentive is keeping you playing, not keeping you losing.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {/* Competitors */}
            <FadeIn delay={0.1} style={{ flex: "1 1 300px" }}>
              <div style={{
                background: C.surfaceHover, border: `1px solid ${C.border}`,
                borderRadius: 20, padding: "36px 28px", height: "100%",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.under, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 28 }}>
                  Other DFS Apps
                </div>
                {[
                  "You play against the house",
                  "Need all picks correct to win anything",
                  "6x payout sounds great... until you realize you win ~12% of the time",
                  "The app profits when you lose",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
                    <span style={{ color: C.under, fontSize: 15, flexShrink: 0, marginTop: 2 }}>✕</span>
                    <span style={{ fontSize: 15, color: C.textSecondary, lineHeight: 1.55 }}>{item}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
            {/* Parlaye */}
            <FadeIn delay={0.2} style={{ flex: "1 1 300px" }}>
              <div style={{
                background: C.card, border: `1.5px solid rgba(25, 224, 148, 0.3)`,
                borderRadius: 20, padding: "36px 28px", height: "100%",
                boxShadow: "0 0 32px rgba(25, 224, 148, 0.07)",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 28 }}>
                  Parlaye
                </div>
                {[
                  "You play against one real person",
                  "Points for each correct pick, so partial wins count",
                  "1v1 means you always start with at least 50% odds",
                  "Our players always win. ",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
                    <span style={{ color: C.primary, fontSize: 15, flexShrink: 0, marginTop: 2 }}>✓</span>
                    <span style={{ fontSize: 15, color: C.textSecondary, lineHeight: 1.55 }}>{item}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: FEATURES ── */}
      <section style={{ background: C.bg }}>
        <div style={sectionInner}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h2 style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800,
                letterSpacing: "-0.02em", marginBottom: 14, color: C.textPrimary,
              }}>
                Built for people who are{" "}
                <span style={{ color: C.primary }}>tired of losing</span>
              </h2>
              <p style={{ fontSize: 18, color: C.textSecondary, maxWidth: 560, margin: "0 auto", lineHeight: 1.6 }}>
                We redesigned daily fantasy from scratch around one idea: players should compete against each other, not against the platform.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <FeatureCard icon="⚖️" title="Fair by Design" description="With 1v1 matchups, you always start with at least a 50/50 shot. No more 12.5% win rates disguised as big multipliers." delay={0.1} />
            <FeatureCard icon="🎯" title="Partial Wins Count" description="Our points system rewards every correct pick. Go 2 out of 3? You can still win if your correct picks were worth more points." delay={0.2} />
            <FeatureCard icon="🤝" title="Challenge Friends" description="Skip the matchmaking. Add friends directly and challenge them to free or paid head-to-head contests on tonight's games." delay={0.3} />
            <FeatureCard icon="🏈" title="10+ Sports" description="NBA, NHL, NFL, Premier League, La Liga, Bundesliga, MLB, UFC, and more. If there's a stat line, we've probably got it." delay={0.1} />
            <FeatureCard icon="🆓" title="Free Contests" description="Not ready to put money on it? Play free contests from anywhere in North America to learn the ropes and compete for bragging rights." delay={0.2} />
            <FeatureCard icon="🔒" title="Transparent and Secure" description="Every contest, every match, every payout is visible to you. Encrypted payments, fast withdrawals, and fully compliant with gaming regulations." delay={0.3} />
          </div>
        </div>
      </section>

      {/* ── SECTION 6: POINTS SYSTEM ── */}
      <section style={{ background: C.surface }}>
        <div style={sectionInner}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h2 style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800,
                letterSpacing: "-0.02em", marginBottom: 14, color: C.textPrimary,
              }}>
                How points work
              </h2>
              <p style={{ fontSize: 18, color: C.textSecondary, maxWidth: 580, margin: "0 auto", lineHeight: 1.6 }}>
                Every prop has a point value based on its difficulty. Harder calls pay more points. You don't need to be perfect, you just need to outscore your opponent.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div style={{
              background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 24,
              padding: "40px 36px", maxWidth: 640, margin: "0 auto",
            }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: C.textMuted,
                letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 28,
              }}>
                Example Contest
              </div>

              {/* You */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, background: C.primaryMuted,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800, color: C.primary, fontFamily: "'Space Mono', monospace",
                  }}>Y</div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary }}>You</span>
                  <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: C.primary, fontFamily: "'Space Mono', monospace" }}>430</span>
                  <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>pts</span>
                </div>
                {YOU_PICKS.map((p, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", borderRadius: 10,
                    background: i % 2 === 0 ? C.surfaceHover : "transparent", marginBottom: 4,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {p.jersey && (
                        <div style={{
                          width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                          background: p.teamColor || C.surfaceHover,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, color: "#ffffff",
                          fontFamily: "'Space Mono', monospace",
                        }}>{p.jersey}</div>
                      )}
                      <span style={{ fontSize: 14, color: p.correct ? C.primary : C.under }}>{p.correct ? "✓" : "✕"}</span>
                      <span style={{ fontSize: 14, color: C.textSecondary }}>{p.pick}</span>
                    </div>
                    <span style={{
                      fontSize: 14, fontWeight: 700, fontFamily: "'Space Mono', monospace",
                      color: p.correct ? C.primary : C.textMuted,
                      textDecoration: p.correct ? "none" : "line-through",
                    }}>{p.pts}</span>
                  </div>
                ))}
              </div>

              {/* Opponent */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, background: C.underMuted,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800, color: C.under, fontFamily: "'Space Mono', monospace",
                  }}>O</div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary }}>Opponent</span>
                  <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: C.textMuted, fontFamily: "'Space Mono', monospace" }}>195</span>
                  <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>pts</span>
                </div>
                {OPP_PICKS.map((p, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", borderRadius: 10,
                    background: i % 2 === 0 ? C.surfaceHover : "transparent", marginBottom: 4,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {p.jersey && (
                        <div style={{
                          width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                          background: p.teamColor || C.surfaceHover,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, color: "#ffffff",
                          fontFamily: "'Space Mono', monospace",
                        }}>{p.jersey}</div>
                      )}
                      <span style={{ fontSize: 14, color: p.correct ? C.primary : C.under }}>{p.correct ? "✓" : "✕"}</span>
                      <span style={{ fontSize: 14, color: C.textSecondary }}>{p.pick}</span>
                    </div>
                    <span style={{
                      fontSize: 14, fontWeight: 700, fontFamily: "'Space Mono', monospace",
                      color: p.correct ? C.primary : C.textMuted,
                      textDecoration: p.correct ? "none" : "line-through",
                    }}>{p.pts}</span>
                  </div>
                ))}
              </div>

              <div style={{
                padding: "16px 20px", borderRadius: 14, textAlign: "center",
                background: C.primaryMuted, border: `1px solid rgba(25, 224, 148, 0.25)`,
              }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: C.primary }}>
                  You went 2/3. Opponent went 1/3. You win.
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── SECTION 7: FAQs ── */}
      <section id="faqs" style={{ background: C.bg }}>
        <div style={sectionInner}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h2 style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800,
                letterSpacing: "-0.02em", color: C.textPrimary,
              }}>FAQs</h2>
            </div>
          </FadeIn>
          <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
            <FAQItem question="Is this sports betting?" answer="No. Parlaye is a daily fantasy sports (DFS) contest platform. DFS is legally classified as a game of skill, not gambling, in most US states and Canadian provinces. You're competing against other players in fantasy contests, not placing bets with a sportsbook." delay={0.05} />
            <FAQItem question="Where can I play?" answer="Free contests are available throughout North America. Paid contests are offered in select US states (AK, CA, FL, GA, IL, KY, MN, NE, NM, NC, ND, OK, OR, RI, SC, SD, TX, UT, WI) and all Canadian provinces except Ontario." delay={0.1} />
            <FAQItem question="How do I actually win?" answer="Pick 3 player props (over or under). Each correct pick earns you the points shown on that prop. Your total points are compared to your opponent's. Higher score wins. You don't need all 3 correct to win, you just need more points than the other person." delay={0.15} />
            <FAQItem question="What does Parlaye charge?" answer="Parlaye takes a small contest fee from each entry. Unlike traditional DFS apps, we don't profit from your losses. Whether you win or lose, our fee is the same. This means we're incentivized to keep the platform fair and fun, not to stack odds against you." delay={0.2} />
            <FAQItem question="Can I play for free?" answer="Yes. Free contests are available from anywhere in North America. They're a great way to learn the format and compete without putting money on the line." delay={0.25} />
            <FAQItem question="Can I play against my friends?" answer="Yes. Add friends directly in the app and challenge them to head-to-head contests on any sport, free or paid." delay={0.3} />
          </div>
        </div>
      </section>

      {/* ── SECTION 8: DOWNLOAD CTA ── */}
      <section id="download" style={{ background: C.surface, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", bottom: -200, left: "50%", transform: "translateX(-50%)",
          width: 800, height: 800, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(25, 224, 148, 0.06) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
        <div style={{ ...sectionInner, textAlign: "center", position: "relative" }}>
          <FadeIn>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800,
              letterSpacing: "-0.02em", marginBottom: 16, color: C.textPrimary,
            }}>
              Stop playing against the house
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p style={{ fontSize: 18, color: C.textSecondary, maxWidth: 500, margin: "0 auto 36px", lineHeight: 1.6 }}>
              Download Parlaye for free and start competing against real people in daily fantasy contests tonight.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
              <a
                href="https://apps.apple.com/us/app/parlaye/id1584562998"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: `linear-gradient(135deg, ${C.primary}, #0fa968)`,
                  color: C.bg, padding: "16px 40px", borderRadius: 14,
                  fontSize: 17, fontWeight: 700, textDecoration: "none",
                  boxShadow: `0 4px 20px ${C.primaryGlow}`, display: "inline-block",
                }}
              >
                Download for iOS
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.parlaye.parlaye"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: C.primary, padding: "16px 40px", borderRadius: 14,
                  fontSize: 17, fontWeight: 600, textDecoration: "none",
                  border: `1.5px solid rgba(25, 224, 148, 0.3)`, display: "inline-block",
                }}
              >
                Download for Android
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FOLLOW US (unchanged) ── */}
      <div
        className="flex flex-col items-center justify-center w-full py-20 gap-16"
        id="contact"
      >
        <HeroHeader
          roundedText="STAY CONNECTED"
          title="FOLLOW US"
          subtitle="Follow us for updates, tips, and exclusive challenges. Be part of the Parlay community!"
          variant="secondary"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 sm:gap-12 md:gap-16 w-full max-w-4xl px-4">
          <a href="https://discord.gg/gHqZVnR8CB" target="_blank" rel="noopener noreferrer" className="flex justify-center transition-transform duration-200 hover:scale-110">
            <img src={DiscordIcon} alt="Discord" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
          </a>
          <a href="https://www.instagram.com/parlayeofficial/" target="_blank" rel="noopener noreferrer" className="flex justify-center transition-transform duration-200 hover:scale-110">
            <img src={InstagramIcon} alt="Instagram" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
          </a>
          <a href="https://www.linkedin.com/company/parlaye-inc/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="flex justify-center transition-transform duration-200 hover:scale-110">
            <img src={LinkedinIcon} alt="LinkedIn" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
          </a>
          <a href="https://www.tiktok.com/@parlaye_official" target="_blank" rel="noopener noreferrer" className="flex justify-center transition-transform duration-200 hover:scale-110">
            <img src={TiktokIcon} alt="TikTok" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
          </a>
          <a href="https://twitter.com/ParlayeOfficial" target="_blank" rel="noopener noreferrer" className="flex justify-center transition-transform duration-200 hover:scale-110">
            <img src={XIcon} alt="X" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
          </a>
        </div>
      </div>

    </div>
  );
}
